import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Typography } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type {
    RowDrawerBarcodeFixModel as BarcodeFix,
    RowDrawerBarcodeFixCode,
} from './rowDrawerResolutions';

type RowDrawerBarcodeFixProps = {
    fix: BarcodeFix;
    onApply: (code: RowDrawerBarcodeFixCode) => void;
};

/**
 * A barcode fix for the row's group inside the problem card (bos-app#228,
 * US-07): an IMEI typed in Barcode (`BARCODE_LOOKS_LIKE_IMEI`) offers
 * **Move to Lot number**; a barcode on several SKUs
 * (`BARCODE_SHARED_ACROSS_SKUS`) names it, says the first SKU by row keeps it
 * and the others lose it, and offers **Keep it on CAS-0063**, that SKU
 * (the only one offered). Either is the bulk fix's default for this group alone.
 */
export const RowDrawerBarcodeFix: React.FC<RowDrawerBarcodeFixProps> = ({
    fix,
    onApply,
}) => {
    const { t } = useTranslation('pages');
    const isImei = fix.code === 'BARCODE_LOOKS_LIKE_IMEI';

    return (
        <div
            className="mt-2 flex w-full flex-col gap-2"
            {...getNodeTestIdProp(
                `catalog-import-row-drawer-barcode-fix-${fix.code}`,
            )}
        >
            <Typography type="body-sm" weight="medium">
                {isImei
                    ? t('catalogImport.products.drawer.fix.imei.title')
                    : t(
                          'catalogImport.products.drawer.fix.sharedBarcode.title',
                          {
                              barcode: fix.barcode,
                          },
                      )}
            </Typography>
            {isImei ? null : (
                <Typography type="body-sm">
                    {t('catalogImport.products.drawer.fix.sharedBarcode.intro')}
                </Typography>
            )}
            <div>
                <Button
                    onPress={() => onApply(fix.code)}
                    size="sm"
                    variant="secondary"
                >
                    {isImei
                        ? t('catalogImport.products.drawer.fix.imei.move')
                        : t(
                              'catalogImport.products.drawer.fix.sharedBarcode.keepOn',
                              { sku: fix.sku },
                          )}
                </Button>
            </div>
        </div>
    );
};
